/**
 * Модуль для управления боевым модальным окном
 */

/**
 * Создает менеджер для управления боевым модальным окном
 */
export function createBattleModalManager({
  getBattleState,
  renderBattlePanels,
  renderBattleLog,
  renderBattleStage,
  renderBattleProgress,
  clearPlaybackTimer,
  clearBattleTimers,
  hideWinnerBanner,
  resetBattleState,
  renderBattle
}) {
  /**
   * Открывает модальное окно боя
   */
  function openBattleModal() {
    const modal = document.getElementById("battle-modal");
    const playback = document.getElementById("battle-playback");
    const battleState = getBattleState();
    
    if (modal) {
      if (!modal.classList.contains("active")) {
        modal.classList.add("active");
        document.body.classList.add("battle-modal-open");
      }
    }
    
    // Активируем контейнер воспроизведения боя, если есть данные
    if (playback && battleState && battleState.timeline && battleState.timeline.length > 0) {
      playback.classList.add("active");
    }
  }

  /**
   * Закрывает модальное окно боя
   * @param {Object} options - Опции закрытия
   * @param {boolean} options.reset - Сбрасывать ли состояние боя (по умолчанию true)
   */
  function closeBattleModal({ reset = true } = {}) {
    const modal = document.getElementById("battle-modal");
    const playback = document.getElementById("battle-playback");
    
    if (modal) {
      if (modal.classList.contains("active")) {
        modal.classList.remove("active");
        document.body.classList.remove("battle-modal-open");
      }
    }
    
    // Деактивируем контейнер воспроизведения боя
    if (playback) {
      playback.classList.remove("active");
    }
    
    if (reset) {
      resetBattleState();
    } else {
      clearPlaybackTimer();
      clearBattleTimers();
    }
  }

  /**
   * Сбрасывает состояние боя
   */
  function resetBattleStateInternal() {
    if (resetBattleState) {
      resetBattleState();
    } else {
      // Fallback: базовый сброс
      const battleState = getBattleState();
      if (battleState) {
        clearPlaybackTimer();
        clearBattleTimers();
        hideWinnerBanner();
        
        battleState.battle = null;
        battleState.panels = [];
        battleState.timeline = [];
        battleState.currentStep = 0;
        battleState.isPlaying = false;
        battleState.lastRenderedStep = -1;
        battleState.winner = null;
        battleState.summary = null;
        battleState.telemetryByRound = {};
        
        // Не вызываем renderBattle после очистки данных - это вызовет ошибку
        // renderBattle будет вызван позже, когда данные будут загружены
      }
    }
  }

  /**
   * Очищает таймеры боя и скрывает countdown
   */
  function clearBattleTimersInternal() {
    if (clearBattleTimers) {
      clearBattleTimers();
    } else {
      // Fallback: базовая очистка
      const battleState = getBattleState();
      if (battleState && battleState.autoTimeouts) {
        battleState.autoTimeouts.forEach((timeout) => clearTimeout(timeout));
        battleState.autoTimeouts = [];
      }
      
      const countdown = document.getElementById("battle-countdown");
      if (countdown) {
        countdown.style.display = "none";
        const text = countdown.querySelector(".battle-countdown-text");
        if (text) {
          text.textContent = "";
        }
        countdown.classList.remove(
          "battle-countdown--large",
          "battle-countdown--small",
          "battle-countdown--fatal"
        );
      }
      
      hideWinnerBanner();
    }
  }

  /**
   * Очищает таймер воспроизведения
   */
  function clearPlaybackTimerInternal() {
    if (clearPlaybackTimer) {
      clearPlaybackTimer();
    } else {
      // Fallback: базовая очистка
      const battleState = getBattleState();
      if (battleState && battleState.timer) {
        clearTimeout(battleState.timer);
        battleState.timer = null;
      }
    }
  }

  /**
   * Скрывает баннер победителя
   */
  function hideWinnerBannerInternal() {
    if (hideWinnerBanner) {
      hideWinnerBanner();
    } else {
      // Fallback: базовая очистка
      const banner = document.getElementById("battle-winner-banner");
      if (banner) {
        banner.classList.remove(
          "active",
          "battle-winner-banner--team-a",
          "battle-winner-banner--team-b"
        );
        const name = banner.querySelector(".battle-winner-name");
        const creature = banner.querySelector(".battle-winner-creature");
        if (name) {
          name.textContent = "—";
        }
        if (creature) {
          creature.textContent = "";
          creature.style.display = "none";
        }
      }
    }
  }

  /**
   * Обновляет рендеринг боя
   */
  function renderBattleInternal() {
    // Проверяем наличие данных перед рендерингом
    const battleState = getBattleState();
    if (!battleState || !battleState.panels || battleState.panels.length === 0) {
      console.warn('[battleModal] No battle data to render');
      return;
    }
    
    if (renderBattle) {
      renderBattle();
    } else {
      // Fallback: базовый рендеринг
      try {
        if (renderBattlePanels) renderBattlePanels();
        if (renderBattleLog) renderBattleLog();
        if (renderBattleStage) renderBattleStage();
        if (renderBattleProgress) renderBattleProgress();
      } catch (error) {
        console.error('[battleModal] Error rendering battle:', error);
      }
    }
  }

  return {
    openBattleModal,
    closeBattleModal,
    resetBattleState: resetBattleStateInternal,
    clearBattleTimers: clearBattleTimersInternal,
    clearPlaybackTimer: clearPlaybackTimerInternal,
    hideWinnerBanner: hideWinnerBannerInternal,
    renderBattle: renderBattleInternal
  };
}

